
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, ArrowUpDown, X, Star, Percent, Package, Store as StoreIcon, Tag, Loader2, SparklesIcon, BrainCircuit } from 'lucide-react';
import { Product, CategoryDoc, Store } from '../types';
import { ProductCardLoader } from './ProductCardLoader';
import { OptimizedImage } from './OptimizedImage';
import { collection, query, where, getDocs, onSnapshot, db, doc, getDoc } from '../firebase';
import { useUser } from '../contexts/UserContext';
import { Toast } from './Toast';

interface SearchPageProps {
  isDark: boolean;
  onOpenDetails?: (product: Product) => void;
  onAddToCart?: (e: React.MouseEvent, product: Product) => void;
  addedToCartMap?: { [key: number]: boolean };
}

// ─── Smart Search Engine ────────────────────────────────────
const normalize = (str: string) => str.toLowerCase().replace(/[أإآ]/g, 'ا').replace(/[ة]/g, 'ه').replace(/[ى]/g, 'ي').trim();

const levenshtein = (a: string, b: string): number => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const m = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      m[i][j] = a[i - 1] === b[j - 1] ? m[i - 1][j - 1] : 1 + Math.min(m[i - 1][j], m[i][j - 1], m[i - 1][j - 1]);
  return m[a.length][b.length];
};

const fuzzyMatch = (text: string, query: string): boolean => {
  if (!text || !query) return false;
  const t = normalize(text);
  const q = normalize(query);
  if (t.includes(q)) return true;
  // Check each word
  const words = t.split(/\s+/);
  for (const word of words) {
    if (word.length >= 3 && q.length >= 3) {
      const dist = levenshtein(word.substring(0, Math.max(q.length, 3)), q);
      if (dist <= Math.floor(q.length / 3)) return true;
    }
  }
  return false;
};

// ─── Ranking Logic ──────────────────────────────────────────
const scoreProduct = (product: Product, tokens: string[], stores: Store[], categories: CategoryDoc[]): number => {
  let score = 0;
  const storeName = stores.find(s => s.id === product.storeId)?.name || product.shopName || '';
  const catName = categories.find(c => c.id === product.mainCategory)?.name || product.category || '';

  for (const token of tokens) {
    const nToken = normalize(token);
    const nName = normalize(product.name);
    const nDesc = normalize(product.description || '');
    const nStore = normalize(storeName);
    const nCat = normalize(catName);
    const nSubCat = normalize(product.subCategory || '');

    // Exact match in name (highest priority)
    if (nName.includes(nToken)) score += 100;
    // Fuzzy match name
    else if (fuzzyMatch(product.name, token)) score += 60;

    // Description match
    if (nDesc.includes(nToken)) score += 40;
    else if (fuzzyMatch(product.description || '', token)) score += 20;

    // Store name match
    if (nStore.includes(nToken)) score += 50;
    else if (fuzzyMatch(storeName, token)) score += 30;

    // Category match
    if (nCat.includes(nToken)) score += 45;
    if (nSubCat.includes(nToken)) score += 35;

    // Sections match (العروض, الرئيسية, etc.)
    if (product.showInSections?.some(s => normalize(s).includes(nToken))) score += 30;

    // Variant colors & sizes
    if (product.variants?.some(v => normalize(v.color || '').includes(nToken) || normalize(v.size || '').includes(nToken))) score += 25;
    if (product.variantColors?.some(vc => normalize(vc.color).includes(nToken) || vc.sizes.some(s => normalize(s.size).includes(nToken)))) score += 25;

    // Shop subcategory
    if (normalize(product.shopSubCategory || '').includes(nToken)) score += 20;
  }

  return score;
};

// ─── Sort Options ───────────────────────────────────────────
const SORT_OPTIONS = [
  { id: 'relevance', label: 'الأكثر صلة', icon: '🎯' },
  { id: 'price_low', label: 'الأقل سعراً', icon: '💰' },
  { id: 'price_high', label: 'الأعلى سعراً', icon: '💎' },
  { id: 'rating', label: 'الأعلى تقييماً', icon: '⭐' },
  { id: 'newest', label: 'الأحدث', icon: '🆕' },
  { id: 'popular', label: 'الأكثر مبيعاً', icon: '🔥' },
  { id: 'discount', label: 'أعلى خصم', icon: '🏷️' },
];

export const SearchPage: React.FC<SearchPageProps> = ({
  isDark,
  onOpenDetails, // We ignore the default empty props if they are empty
  onAddToCart,
  addedToCartMap
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get('q') || '';

  const { addToCart } = useUser();
  const [showToast, setShowToast] = useState(false);
  const [localAddedToCartMap, setLocalAddedToCartMap] = useState<{ [key: number]: boolean }>({});

  const handleAddToCart = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    if (onAddToCart && onAddToCart.toString() !== '() => { }' && onAddToCart.toString() !== '()=>{}') {
      onAddToCart(e, product);
    } else {
      addToCart(product, {
        color: (product as any).selectedColor,
        size: (product as any).selectedSize
      });
      setShowToast(true);
      setLocalAddedToCartMap(prev => ({ ...prev, [product.id as number]: true }));
      setTimeout(() => {
        setLocalAddedToCartMap(prev => ({ ...prev, [product.id as number]: false }));
      }, 2000);
    }
  };

  const handleOpenDetails = (product: Product) => {
    if (onOpenDetails && onOpenDetails.toString() !== '() => { }' && onOpenDetails.toString() !== '()=>{}') {
      onOpenDetails(product);
    } else {
      navigate(`/product/${product.id}`);
    }
  };

  const finalAddedToCartMap = addedToCartMap && Object.keys(addedToCartMap).length > 0 ? addedToCartMap : localAddedToCartMap;

  const [searchTerm, setSearchTerm] = useState(queryParam);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryDoc[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Search Results
  const [matchedCategories, setMatchedCategories] = useState<CategoryDoc[]>([]);
  const [matchedStores, setMatchedStores] = useState<Store[]>([]);

  // Sort
  const [sortBy, setSortBy] = useState('relevance');
  const [showSortPopup, setShowSortPopup] = useState(false);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [priceMin, setPriceMin] = useState<number | ''>('');
  const [priceMax, setPriceMax] = useState<number | ''>('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [onlyDiscount, setOnlyDiscount] = useState(false);
  const [onlyInStock, setOnlyInStock] = useState(false);


  // Load all products, categories, stores once
  useEffect(() => {
    setLoading(true);

    const unsubProducts = onSnapshot(collection(db, 'products'), (snap) => {
      const list: Product[] = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as Product));
      setAllProducts(list.filter(p => p.isActive !== false));
      setLoading(false);
    });

    const unsubCats = onSnapshot(collection(db, 'categories'), (snap) => {
      const list: CategoryDoc[] = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as CategoryDoc));
      setCategories(list.filter(c => c.isActive));
    });

    const unsubStores = onSnapshot(collection(db, 'stores'), (snap) => {
      const list: Store[] = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() } as Store));
      setStores(list);
    });

    return () => { unsubProducts(); unsubCats(); unsubStores(); };
  }, []);

  // Perform search whenever query or data changes
  const [aiRecommendation, setAiRecommendation] = useState('');

  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      setMatchedCategories([]);
      setMatchedStores([]);
      setAiRecommendation('');
      return;
    }

    setLoading(true);
    try {
      // 1. Call AI Search API
      const response = await fetch(`/api/search?q=${encodeURIComponent(term)}`);
      if (!response.ok) throw new Error('Search failed');

      const aiData = await response.json();
      setAiRecommendation(aiData.recommendationText || '');

      // 2. Filter Results based on AI response
      const matchedShpIds = new Set(aiData.shopIds?.map(String) || []);
      const aiResults = (aiData.productIds || [])
        .map(id => allProducts.find(p => String(p.id) === String(id) || p.docId === id))
        .filter(p => p !== undefined) as Product[];

      const aiStores = stores.filter(s => matchedShpIds.has(String(s.id)));

      // 3. Fallback: If AI returns nothing, use legacy fuzzy match for safety
      if (aiResults.length === 0 && aiStores.length === 0) {
        const tokens = term.trim().split(/\s+/).filter(t => t.length > 0);

        let scored = allProducts.map(product => ({
          product,
          score: scoreProduct(product, tokens, stores, categories)
        })).filter(item => item.score > 0);

        // Apply filters
        if (priceMin !== '') scored = scored.filter(i => i.product.price >= priceMin);
        if (priceMax !== '') scored = scored.filter(i => i.product.price <= priceMax);
        if (selectedCategory) scored = scored.filter(i => i.product.mainCategory === selectedCategory);
        if (selectedStore) scored = scored.filter(i => String(i.product.storeId) === String(selectedStore));

        setResults(scored.sort((a, b) => b.score - a.score).map(s => s.product));
        setMatchedStores(stores.filter(store => tokens.some(token => fuzzyMatch(store.name, token))));
      } else {
        setResults(aiResults);
        setMatchedStores(aiStores);
      }

    } catch (error) {
      console.error('AI Search Error, using fallback:', error);
      // Fallback logic inside catch...
      const tokens = term.trim().split(/\s+/).filter(t => t.length > 0);
      setResults(allProducts.filter(p => tokens.some(token => fuzzyMatch(p.name, token))));
    } finally {
      setLoading(false);
    }
  }, [allProducts, stores, categories, priceMin, priceMax, selectedCategory, selectedStore, minRating, onlyDiscount, onlyInStock, sortBy]);


  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm, performSearch]);

  // Sync URL query
  useEffect(() => {
    if (queryParam && queryParam !== searchTerm) {
      setSearchTerm(queryParam);
    }
  }, [queryParam]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`, { replace: true });
    }
  };


  // Count active filters
  const activeFilterCount = [
    priceMin !== '',
    priceMax !== '',
    selectedCategory !== '',
    selectedStore !== '',
    minRating > 0,
    onlyDiscount,
    onlyInStock
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setPriceMin(''); setPriceMax(''); setSelectedCategory(''); setSelectedStore(''); setMinRating(0); setOnlyDiscount(false); setOnlyInStock(false);
  };

  const removeFilter = (filter: string) => {
    switch (filter) {
      case 'price': setPriceMin(''); setPriceMax(''); break;
      case 'category': setSelectedCategory(''); break;
      case 'store': setSelectedStore(''); break;
      case 'rating': setMinRating(0); break;
      case 'discount': setOnlyDiscount(false); break;
      case 'stock': setOnlyInStock(false); break;
    }
  };

  // Active filter chips
  const getActiveFilterChips = () => {
    const chips: { id: string; label: string }[] = [];
    if (priceMin !== '' || priceMax !== '') chips.push({ id: 'price', label: `السعر: ${priceMin || 0} - ${priceMax || '∞'} ج.م` });
    if (selectedCategory) chips.push({ id: 'category', label: `الفئة: ${categories.find(c => c.id === selectedCategory)?.name || selectedCategory}` });
    if (selectedStore) chips.push({ id: 'store', label: `المحل: ${stores.find(s => String(s.id) === String(selectedStore))?.name || selectedStore}` });
    if (minRating > 0) chips.push({ id: 'rating', label: `التقييم: ${minRating}+ ⭐` });
    if (onlyDiscount) chips.push({ id: 'discount', label: 'عروض فقط' });
    if (onlyInStock) chips.push({ id: 'stock', label: 'متاح فقط' });
    return chips;
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f1115]' : 'bg-[#f8f9ff]'}`} style={{ direction: 'rtl' }}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-24 md:pt-28 pb-[72px] md:pb-4">

        {/* ─── Search Bar ──────────── */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className={`flex items-center gap-3 rounded-2xl px-4 md:px-6 py-3 md:py-4 border shadow-lg backdrop-blur-xl transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
            <Search className={`shrink-0 ${isDark ? 'text-white/40' : 'text-slate-400'}`} size={22} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ابحث عن منتج، محل، فئة، لون..."
              className={`flex-1 bg-transparent outline-none font-bold text-base md:text-lg ${isDark ? 'text-white placeholder:text-white/30' : 'text-slate-800 placeholder:text-slate-400'}`}
              autoFocus
            />
            {searchTerm && (
              <button type="button" onClick={() => { setSearchTerm(''); setResults([]); }} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                <X size={18} className={isDark ? 'text-white/40' : 'text-slate-400'} />
              </button>
            )}
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl font-black text-sm transition-colors active:scale-95 shadow-lg shadow-blue-600/20">
              بحث
            </button>
          </div>
        </form>

        {/* ─── Sort & Filter Buttons ──── */}
        <div className="flex items-center gap-2 mb-4">
          {/* Sort Button */}
          <div className="relative">
            <button
              onClick={() => { setShowSortPopup(!showSortPopup); setShowFilters(false); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm border transition-all active:scale-95 ${isDark
                ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
                }`}
            >
              <ArrowUpDown size={16} />
              <span className="hidden sm:inline">ترتيب</span>
              <span className="text-xs opacity-60">{SORT_OPTIONS.find(s => s.id === sortBy)?.icon}</span>
            </button>

            {/* Sort Popup */}
            {showSortPopup && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSortPopup(false)} />
                <div className={`absolute top-full mt-2 right-0 z-50 w-56 rounded-2xl border shadow-2xl overflow-hidden animate-fade-in-up ${isDark ? 'bg-[#1a1c1e] border-white/10' : 'bg-white border-slate-200'}`}>
                  {SORT_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      onClick={() => { setSortBy(option.id); setShowSortPopup(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-bold transition-colors ${sortBy === option.id
                        ? isDark ? 'bg-blue-600/20 text-blue-400' : 'bg-blue-50 text-blue-600'
                        : isDark ? 'text-white/80 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                      <span className="text-lg">{option.icon}</span>
                      <span>{option.label}</span>
                      {sortBy === option.id && <span className="mr-auto text-blue-500">✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Filter Button */}
          <button
            onClick={() => { setShowFilters(!showFilters); setShowSortPopup(false); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm border transition-all active:scale-95 relative ${isDark
              ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
              } ${showFilters ? isDark ? '!border-blue-500/50 !bg-blue-600/10' : '!border-blue-500 !bg-blue-50' : ''}`}
          >
            <SlidersHorizontal size={16} />
            <span className="hidden sm:inline">فلتر</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-blue-600 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-lg">
                {activeFilterCount}
              </span>
            )}
          </button>



          {/* Results count */}
          {searchTerm && !loading && (
            <span className={`text-sm font-bold mr-auto ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              {results.length} نتيجة
            </span>
          )}
        </div>

        {/* ─── Active Filter Chips ──── */}
        {getActiveFilterChips().length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {getActiveFilterChips().map(chip => (
              <span
                key={chip.id}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${isDark ? 'bg-blue-600/10 border-blue-500/20 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-600'
                  }`}
              >
                {chip.label}
                <X size={12} className="cursor-pointer hover:text-red-500 transition-colors" onClick={() => removeFilter(chip.id)} />
              </span>
            ))}
            <button onClick={clearAllFilters} className="text-xs font-bold text-red-500 hover:underline px-2">
              مسح الكل
            </button>
          </div>
        )}

        {/* ─── AI Recommendation ────── */}
        {aiRecommendation && !loading && (
          <div className={`mb-6 p-4 md:p-6 rounded-2xl border-2 border-blue-500/30 bg-blue-500/5 backdrop-blur-md animate-fade-in`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/40">
                <BrainCircuit className="text-white" size={18} />
              </div>
              <h3 className={`font-black text-sm md:text-base ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>ترشيح العميل الذكي (AI)</h3>
            </div>
            <p className={`text-sm md:text-lg font-bold leading-relaxed ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
              {aiRecommendation}
            </p>
          </div>
        )}

        {/* ─── Filters Panel ──────── */}
        {showFilters && (
          <div className={`mb-5 p-5 rounded-2xl border animate-fade-in-up ${isDark ? 'bg-[#1a1c1e] border-white/10' : 'bg-white border-slate-200 shadow-lg'}`}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Price Range */}
              <div className="space-y-2 col-span-2 md:col-span-1">
                <label className={`text-xs font-black uppercase ${isDark ? 'text-white/50' : 'text-slate-500'}`}>💰 نطاق السعر</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={priceMin}
                    onChange={e => setPriceMin(e.target.value ? Number(e.target.value) : '')}
                    className={`w-full px-3 py-2.5 rounded-xl border text-sm font-bold ${isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-slate-50 border-slate-200'}`}
                    placeholder="من"
                  />
                  <input
                    type="number"
                    value={priceMax}
                    onChange={e => setPriceMax(e.target.value ? Number(e.target.value) : '')}
                    className={`w-full px-3 py-2.5 rounded-xl border text-sm font-bold ${isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-slate-50 border-slate-200'}`}
                    placeholder="إلى"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <label className={`text-xs font-black uppercase ${isDark ? 'text-white/50' : 'text-slate-500'}`}>📂 الفئة</label>
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm font-bold ${isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-slate-50 border-slate-200'}`}
                >
                  <option value="">الكل</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              {/* Store */}
              <div className="space-y-2">
                <label className={`text-xs font-black uppercase ${isDark ? 'text-white/50' : 'text-slate-500'}`}>🏪 المحل</label>
                <select
                  value={selectedStore}
                  onChange={e => setSelectedStore(e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm font-bold ${isDark ? 'bg-black/30 border-white/10 text-white' : 'bg-slate-50 border-slate-200'}`}
                >
                  <option value="">الكل</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {/* Rating */}
              <div className="space-y-2">
                <label className={`text-xs font-black uppercase ${isDark ? 'text-white/50' : 'text-slate-500'}`}>⭐ التقييم</label>
                <div className="flex gap-1">
                  {[0, 3, 3.5, 4, 4.5, 5].map(r => (
                    <button
                      key={r}
                      onClick={() => setMinRating(r)}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-all ${minRating === r
                        ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-500'
                        : isDark ? 'border-white/10 text-white/50 hover:bg-white/5' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                      {r === 0 ? 'الكل' : `${r}+`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggle Filters */}
              <div className="space-y-3 col-span-2 md:col-span-1">
                <label className={`text-xs font-black uppercase ${isDark ? 'text-white/50' : 'text-slate-500'}`}>⚡ خيارات</label>
                <div className="space-y-2">
                  <label className={`flex items-center gap-3 cursor-pointer p-2 rounded-xl border transition-all ${onlyDiscount ? isDark ? 'bg-green-900/20 border-green-500/30' : 'bg-green-50 border-green-300' : isDark ? 'border-white/10' : 'border-slate-200'}`}>
                    <input type="checkbox" checked={onlyDiscount} onChange={e => setOnlyDiscount(e.target.checked)} className="w-4 h-4 accent-green-500 rounded" />
                    <span className={`text-sm font-bold ${isDark ? 'text-white/70' : 'text-slate-600'}`}>🏷️ عروض و خصومات فقط</span>
                  </label>
                  <label className={`flex items-center gap-3 cursor-pointer p-2 rounded-xl border transition-all ${onlyInStock ? isDark ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-300' : isDark ? 'border-white/10' : 'border-slate-200'}`}>
                    <input type="checkbox" checked={onlyInStock} onChange={e => setOnlyInStock(e.target.checked)} className="w-4 h-4 accent-blue-500 rounded" />
                    <span className={`text-sm font-bold ${isDark ? 'text-white/70' : 'text-slate-600'}`}>📦 متوفر في المخزن فقط</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── Stores & Categories Results ──── */}
        {searchTerm.trim() && (matchedStores.length > 0 || matchedCategories.length > 0) && (
          <div className="mb-8 space-y-6">
            {/* Matched Stores */}
            {matchedStores.length > 0 && (
              <div className="space-y-3">
                <h3 className={`text-sm font-black flex items-center gap-2 px-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  <StoreIcon size={16} /> المحلات المطابقة
                </h3>
                <div className="flex flex-wrap gap-3">
                  {matchedStores.map(store => (
                    <div
                      key={store.id}
                      onClick={() => navigate(`/shop/${store.id}`)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border cursor-pointer transition-all hover:scale-105 active:scale-95 ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-slate-100 shadow-sm hover:shadow-md'}`}
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden border border-white/10">
                        <OptimizedImage src={store.logo} alt={store.name} aspectRatio="1/1" />
                      </div>
                      <div>
                        <p className="font-black text-sm">{store.name}</p>
                        <p className={`text-[10px] font-bold ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{store.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Matched Categories */}
            {matchedCategories.length > 0 && (
              <div className="space-y-3">
                <h3 className={`text-sm font-black flex items-center gap-2 px-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                  <Tag size={16} /> الفئات المطابقة
                </h3>
                <div className="flex flex-wrap gap-2">
                  {matchedCategories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => navigate(`/category/${cat.id}`)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full border font-bold text-xs transition-all hover:scale-105 active:scale-95 ${isDark ? 'bg-blue-600/10 border-blue-500/20 text-blue-400 hover:bg-blue-600/20' : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100'}`}
                    >
                      <SparklesIcon size={12} />
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={`h-px w-full ${isDark ? 'bg-white/5' : 'bg-slate-100'}`} />
          </div>
        )}

        {/* ─── AI Recommendation Card ─── */}
        {aiRecommendation && (
          <div className={`mb-8 p-6 rounded-3xl border relative overflow-hidden group animate-fade-in-up ${isDark ? 'bg-indigo-600/10 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'}`}>
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-2xl ${isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-white text-indigo-600 shadow-sm'}`}>
                <BrainCircuit size={24} />
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <h4 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>اقتراح ذكي</h4>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-200/50 text-indigo-700'}`}>Beta</span>
                </div>
                <p className={`text-base md:text-lg font-bold leading-relaxed ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {aiRecommendation}
                </p>
              </div>
            </div>
            {/* Subtle glow effect */}
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-indigo-500/10 blur-[50px] rounded-full" />
          </div>
        )}

        {/* ─── Results ──────────────── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="animate-spin text-blue-500" size={40} />
            <p className={`font-bold text-sm ${isDark ? 'text-white/40' : 'text-slate-400'}`}>جاري البحث...</p>
          </div>
        ) : results.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {results.map((product, idx) => (
              <ProductCardLoader
                key={product.id}
                product={product}
                isDark={isDark}
                onOpenDetails={handleOpenDetails}
                onAddToCart={handleAddToCart}
                isAdded={!!finalAddedToCartMap[product.id as number]}
                priority={idx < 5}
              />
            ))}
          </div>
        ) : searchTerm.trim() ? (
          <div className="flex flex-col items-center justify-center py-16 md:py-20 gap-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
              <Search size={36} className={isDark ? 'text-slate-700' : 'text-slate-300'} />
            </div>
            <p className={`font-black text-lg ${isDark ? 'text-white/60' : 'text-slate-500'}`}>لا توجد نتائج لـ "{searchTerm}"</p>
            <p className={`font-bold text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>جرب كلمات مختلفة أو غيّر الفلاتر</p>
            {activeFilterCount > 0 && (
              <button onClick={clearAllFilters} className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors active:scale-95">
                مسح كل الفلاتر
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 md:py-24 gap-4">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-blue-50'}`}>
              <Search size={40} className={isDark ? 'text-blue-500/30' : 'text-blue-300'} />
            </div>
            <p className={`font-black text-xl ${isDark ? 'text-white/40' : 'text-slate-400'}`}>ابحث عن أي شيء</p>
            <p className={`font-bold text-sm text-center ${isDark ? 'text-white/20' : 'text-slate-400'}`}>
              اكتب اسم منتج، محل، فئة، أو حتى لون
            </p>
          </div>
        )}

      </div>
      <Toast message="تم إضافة المنتج" isVisible={showToast} onClose={() => setShowToast(false)} isDark={isDark} />
    </div>
  );
};
